import React from 'react';

import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import CircularPrimersVisualization from 'molbio/components/visualizations/CircularPrimersVisualization';
import {
  DEFAULT_HEIGHT,
  DEFAULT_WIDTH,
  VisualizationSequenceSummary,
} from 'molbio/components/visualizations/SequenceVisualizationConstants';
import {getCirclePoint, getTextAnchorForCircularText} from 'molbio/maps/circular/CircularUtil';
import Arc from 'ui-library/svg/Arc';
import Line from 'ui-library/svg/Line';
import {Angle, Point} from 'util/geometry/GeometryConstants';
import {getAngle, isRangeOverlap, polarToCartesian} from 'util/geometry/GeometryUtil';

import DisplayInfoLabel from './DisplayInfoLabel';
import {
  BASE_STROKE_COLOR,
  BASE_STROKE_WIDTH,
  CIRCLE_CENTER,
  FRAGMENT_STROKE_WIDTH,
  RADIUS,
  TEXT_HEIGHT,
  TextPointInfo,
} from './SequenceVisualizationConstants';
import VisualizationContainer from './VisualizationContainer';
import {getDisplayInfos, getIndexForArrow} from './VisualizationUtil';

/**
 * Get the appropriate y-delta for a given angle along a circle.
 *
 * For text values near the bottom of the circle, we shift the text down so the line goes to the
 * top of the text.
 */
function getDy(angle: Angle): number {
  return (Math.PI * 5) / 6 <= angle && angle <= (Math.PI * 7) / 6 ? TEXT_HEIGHT - 5 : 0;
}

/**
 * Returns information about where text should be placed to avoid overlaps
 */
function getCircularIndexTextPointInfos(
  circleCenter: Point,
  start: number,
  end: number,
  length: number
): {start: TextPointInfo; end: TextPointInfo} {
  const lineLength = 15;
  const startPoint = getCirclePoint(CIRCLE_CENTER, start, length, RADIUS + lineLength);
  const endPoint = getCirclePoint(CIRCLE_CENTER, end, length, RADIUS + lineLength);
  let changeInY = 0;
  let changeInX = 0;

  // If both pieces of text are on the same side and their y-values overlap, adjust the
  // y-value of the endPoint slightly.
  if (
    startPoint.x > circleCenter.x === endPoint.x > circleCenter.x &&
    isRangeOverlap(
      {start: startPoint.y, end: startPoint.y + TEXT_HEIGHT},
      {start: endPoint.y, end: endPoint.y + TEXT_HEIGHT}
    )
  ) {
    if (startPoint.y <= endPoint.y) {
      changeInY = TEXT_HEIGHT + 5;
      if (endPoint.y < circleCenter.y) {
        // Adjust x value away from circle
        changeInX = 15 * (endPoint.x > circleCenter.x ? 1 : -1);
      }
    } else {
      changeInY = -TEXT_HEIGHT - 5;
      if (endPoint.y > circleCenter.y) {
        // Adjust x value away from circle
        changeInX = 15 * (endPoint.x > circleCenter.x ? 1 : -1);
      }
    }
  }
  endPoint.x += changeInX;
  endPoint.y += changeInY;
  return {
    start: {
      point: {x: startPoint.x, y: startPoint.y + getDy(getAngle(start, length))},
      anchor: getTextAnchorForCircularText(circleCenter, startPoint),
    },
    end: {
      point: {x: endPoint.x, y: endPoint.y + getDy(getAngle(end, length))},
      anchor: getTextAnchorForCircularText(circleCenter, endPoint),
    },
  };
}

export enum CircularArrowType {
  INSIDE = 'INSIDE',
  OUTSIDE = 'OUTSIDE',
  BOTH = 'BOTH',
}

export function CircularArrow({
  circleCenter,
  radius,
  arrowCenter,
  circleAngle,
  direction,
  stroke,
  strokeWidth,
  arrowType = CircularArrowType.BOTH,
}: {
  circleCenter: Point;
  radius: number;
  arrowCenter: Point;
  circleAngle: Angle;
  direction: StrandOfDoubleStranded;
  stroke: string;
  strokeWidth: number;
  arrowType?: CircularArrowType;
}): JSX.Element {
  const radiusDifference = 4;
  const angleDifference = 0.14;
  const insideCenter = polarToCartesian(
    circleCenter,
    radius - radiusDifference,
    circleAngle - direction * angleDifference
  );
  const outsideCenter = polarToCartesian(
    circleCenter,
    radius + radiusDifference,
    circleAngle - direction * angleDifference
  );

  return (
    <>
      {arrowType !== CircularArrowType.OUTSIDE && (
        <Line
          startPoint={arrowCenter}
          endPoint={insideCenter}
          strokeWidth={strokeWidth}
          stroke={stroke}
          strokeLinecap="round"
        />
      )}
      {arrowType !== CircularArrowType.INSIDE && (
        <Line
          startPoint={arrowCenter}
          endPoint={outsideCenter}
          strokeWidth={strokeWidth}
          stroke={stroke}
          strokeLinecap="round"
        />
      )}
    </>
  );
}

export default function CircularFragmentVisualization({
  sequence,
  fragmentColor,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
}: {
  sequence: VisualizationSequenceSummary;
  fragmentColor: string;
  width?: number;
  height?: number;
}): JSX.Element {
  const {start, end, strand, sequenceLength: length} = sequence;
  if (length === 0) {
    return (
      <VisualizationContainer width={width} height={height}>
        <Arc
          circleCenter={CIRCLE_CENTER}
          radius={RADIUS}
          startAngle={0}
          endAngle={0}
          pathProps={{stroke: BASE_STROKE_COLOR, strokeWidth: BASE_STROKE_WIDTH}}
        />
      </VisualizationContainer>
    );
  }
  const startAngle = getAngle(start % length, length);
  const endAngle = getAngle((end ?? start) % length, length);

  // Since arrowIndex may be equal to length when end = 0 and strand = 1, take the value mod sequence length.
  const arrowIndex = getIndexForArrow(start, end, strand, length) % length;
  const arrowCenter = getCirclePoint(CIRCLE_CENTER, arrowIndex, length, RADIUS);

  const {startDisplayInfo, endDisplayInfo} = getDisplayInfos(sequence);
  const textPointInfos = getCircularIndexTextPointInfos(CIRCLE_CENTER, start, end ?? start, length);
  return (
    <VisualizationContainer width={width} height={height}>
      <DisplayInfoLabel // Text label (enzyme/bases) for fragment start
        sequencePoint={polarToCartesian(CIRCLE_CENTER, RADIUS, startAngle)}
        textPointInfo={textPointInfos.start}
        displayInfo={startDisplayInfo}
      />
      <DisplayInfoLabel // Text label (enzyme/bases) for fragment end
        sequencePoint={polarToCartesian(CIRCLE_CENTER, RADIUS, endAngle)}
        textPointInfo={textPointInfos.end}
        displayInfo={endDisplayInfo}
      />
      <Arc // Arc for the whole "base" sequence (just renders the fragment, not the whole circle)
        circleCenter={CIRCLE_CENTER}
        radius={RADIUS}
        startAngle={endAngle}
        endAngle={startAngle}
        pathProps={{stroke: BASE_STROKE_COLOR, strokeWidth: BASE_STROKE_WIDTH}}
      />
      <Arc // Arc showing which part of the sequence is actually selected as the fragment
        circleCenter={CIRCLE_CENTER}
        radius={RADIUS}
        startAngle={startAngle}
        endAngle={end == null ? startAngle + 0.001 : endAngle}
        pathProps={{stroke: fragmentColor, strokeWidth: FRAGMENT_STROKE_WIDTH}}
      />
      <CircularArrow // Arrow to show directionality
        circleCenter={CIRCLE_CENTER}
        radius={RADIUS}
        arrowCenter={arrowCenter}
        circleAngle={getAngle(arrowIndex, length)}
        direction={strand}
        stroke={fragmentColor}
        strokeWidth={FRAGMENT_STROKE_WIDTH}
      />
      {end != null && (
        <CircularPrimersVisualization
          startDisplayInfo={startDisplayInfo}
          endDisplayInfo={endDisplayInfo}
          startAngle={startAngle}
          endAngle={endAngle}
          circleCenter={CIRCLE_CENTER}
          radius={RADIUS}
          fragmentStrand={strand}
        />
      )}
    </VisualizationContainer>
  );
}
